import React, { useEffect, useState } from "react";
import RulesListDataTable from "../DataGrids/RulesListDataGrid";
import { Box, Fade, IconButton } from "@mui/material";
import { useThemeMode } from "../../contexts/ThemeModeContext";
import { darkModeTokens } from "../../styles/Theme";
import { GRID_CHECKBOX_SELECTION_COL_DEF } from "@mui/x-data-grid-pro";
import useFetchUsersList from "../../hooks/useFetchUsersList";
import Loader from "../Molecules/Loader/Loader";
import { isEmpty } from "lodash";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";

interface MissingRecordsDashboardTabProps {
  setMissingColumns: any;
  missingRowsData: any;
  missingColumnsData: any;
  setMissingRowsData: any;
  columnGroupingModel: any;
  keyColumns: any;
  totalPages: any;
  page: number;
  setPage: any;
  pSize: number;
  setPSize: any;
  isLoading: boolean;
  dashboardData: any;
}

const MissingSnapshotRecordsDashboardTab = ({
  setMissingColumns,
  missingRowsData,
  missingColumnsData,
  setMissingRowsData,
  columnGroupingModel,
  keyColumns,
  totalPages,
  page,
  setPage,
  pSize,
  setPSize,
  isLoading,
  dashboardData,
}: MissingRecordsDashboardTabProps) => {
  const { rowsPerPageOptions } = useRowsPerPage();
  const { mode } = useThemeMode();
  const isDark = mode === "dark";
  const [selectedIds, setSelectedIds] = useState<any>([]);
  const [isAllColumnPinned, setIsAllColumnPinned] = useState(false);

  const [pinnedColumns, setPinnedColumns] = useState({
    left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
    right: [""],
  });

  useEffect(() => {
    setPinnedColumns((prevState) => {
      const updatedLeft = [
        GRID_CHECKBOX_SELECTION_COL_DEF.field,
        ...keyColumns,
      ];
      const updatedRight = [...prevState.right];

      return {
        left: updatedLeft,
        right: updatedRight,
      };
    });
  }, [keyColumns]);

  const handleMoveColumns = () => {
    setPinnedColumns((prevState) => {
      const columnsToAdd = ["current_status", "username", "comment"];
      const isColumnsAvailable = columnsToAdd.every((item) =>
        prevState.right.includes(item),
      );
      const isKeyColumnAvailable = keyColumns.every((item: any) =>
        prevState.left.includes(item),
      );
      setIsAllColumnPinned(isColumnsAvailable);
      if (isColumnsAvailable && isKeyColumnAvailable) {
        return {
          left: prevState.left.filter((item) => !keyColumns.includes(item)),
          right: prevState.right.filter((item) => !columnsToAdd.includes(item)),
        };
      } else {
        return {
          left: [...prevState.left, ...keyColumns],
          right: [...columnsToAdd, ...prevState.right],
        };
      }
    });
  };

  /**
   * Determines if checkbox column should be visible
   *
   * Uses lodash isEmpty() to check if comparison_research_query_results is empty.
   * lodash isEmpty() returns true for: null, undefined, {}, [], "", Map(), Set()
   *
   * Checkbox is VISIBLE when:
   * - missingRowsData has at least one row (length > 0), AND
   * - At least ONE of the following conditions is true:
   *   1. is_issues_data_available === true, OR
   *   2. comparison_research_query_results is a non-empty array or non-empty object
   *
   * Checkbox is HIDDEN when:
   * - missingRowsData has no rows (length === 0), OR
   * - missingRowsData has rows BUT BOTH of the following are true:
   *   1. is_issues_data_available is false/null/undefined, AND
   *   2. comparison_research_query_results is null/undefined/empty array/empty object
   */
  const isCheckboxRequired =
    // First condition: Must have data rows
    Array.isArray(missingRowsData) &&
    missingRowsData.length > 0 &&
    // Second condition: At least one of these must be true
    // Option 1: Issues data is available
    (dashboardData?.is_issues_data_available === true ||
      // Option 2: Comparison research query results exist and are not empty (using lodash isEmpty)
      !isEmpty(dashboardData?.comparison_research_query_results));

  return (
    <>
      {isLoading && (
        <Box className="adhock-query-accordion-wrapper">
          <Loader isLoading={isLoading} />
        </Box>
      )}
      {keyColumns && (
        <>
          <Box className="position-relative">
            {missingRowsData && missingRowsData?.length !== 0 ? (
              <Fade in timeout={300}>
                <Box>
                  <RulesListDataTable
                    selectedIds={selectedIds}
                    setSelectedIds={setSelectedIds}
                    dataRows={missingRowsData || []}
                    dataColumns={missingColumnsData || []}
                    loading={isLoading}
                    disableColumnFilter={true}
                    className="dataTable datagridHeaderColumn no-radius pt-0 bdr-top-0  datatable-column-sep checkbox-result-column pinnedColumnHeaders-bg-height hide-progress-icon"
                    tableHeight={180}
                    disableColumnReorder={true}
                    columnGroupingModel={columnGroupingModel}
                    setResultColumns={setMissingColumns}
                    checkboxSelection={isCheckboxRequired}
                    paginationMode="server"
                    rowCount={totalPages || 0}
                    pageSizeOptions={rowsPerPageOptions}
                    paginationModel={{
                      page: page - 1,
                      pageSize: pSize,
                    }}
                    onPaginationModelChange={(params: any) => {
                      if (
                        params.pageSize !== pSize ||
                        params.page !== page - 1
                      ) {
                        setPage(params.page + 1);
                        setPSize(params.pageSize);
                      }
                    }}
                    pinnedColumns={
                      missingRowsData?.length > 0 ? pinnedColumns : {}
                    }
                    disableColumnPinning={false}
                    singlePageMaxHeightDiff={262}
                    variant="premium"
                    resourcePrefixes={
                      dashboardData?.additional_properties?.resource_prefixes
                    }
                  />
                </Box>
              </Fade>
            ) : (
              <Box
                className="adhock-query-accordion-wrapper"
                sx={{
                  paddingTop: "24px",
                  paddingBottom: "24px",
                  display: "flex",
                  justifyContent: "center",
                  borderTop: "none",
                  backgroundColor: isDark
                    ? darkModeTokens.surfaceInset
                    : "transparent",
                  color: "text.secondary",
                }}
              >
                No data available
              </Box>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default MissingSnapshotRecordsDashboardTab;
